import React, { useState, useEffect, ReactNode } from 'react';
import { Phone, MessageSquare, Mail, Search, X, ChevronRight, CheckCircle, Clock, BarChart3, BookOpen, Zap, User, Plus, ExternalLink, Star, TrendingUp, Send, Sparkles, RefreshCw, Home, Lock, MoreVertical, ArrowLeft, ArrowRight, Menu, PanelRightClose, PanelRightOpen, GripVertical, TrendingDown, Activity, Users, Headphones, AlertTriangle, Award, Target, Brain, Layers, Shield, Info } from 'lucide-react';

// TypeScript interfaces
interface TooltipProps {
  children: ReactNode;
  content: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

interface MLBadgeProps {
  model: string;
  accuracy?: number;
}

interface Tab {
  id: number;
  title: string;
  url: string;
  type: string;
  canClose: boolean;
}

interface PlaybookStep {
  id: number;
  title: string;
  status: 'completed' | 'active' | 'pending';
  duration: string | null;
}

interface TranscriptEntry {
  speaker: string;
  text: string;
  time: string;
}

interface LLMMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatMessage {
  role: 'agent' | 'customer';
  text: string;
  time: string;
}

interface RoutingAnalysis {
  compatibilityScore: number;
  predictedCSAT: number;
  predictedFCR: number;
  predictedAHT: number;
  routingTime: number;
  modelsProcessed: number;
  signalsAnalyzed: number;
}

interface KBArticle {
  id: string;
  title: string;
  category: string;
  lastUpdated: string;
  relevance: number;
  views: number;
  helpful: number;
  content: string;
}

const Tooltip: React.FC<TooltipProps> = ({ children, content, position = 'top' }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  const positionClasses = {
    top: 'bottom-full left-1/2 transform -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 transform -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 transform -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 transform -translate-y-1/2 ml-2'
  };
  
  return (
    <div className="relative inline-block">
      <div 
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>
      {isVisible && (
        <div className={`absolute z-[100] ${positionClasses[position]} w-80 pointer-events-none`}>
          <div className="bg-gray-900 text-white p-3 rounded-lg shadow-2xl border border-gray-700 text-xs">
            {content}
          </div>
        </div>
      )}
    </div>
  );
};

const MLBadge: React.FC<MLBadgeProps> = ({ model, accuracy }) => (
  <div className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
    <Brain className="w-3 h-3" />
    <span>{model}</span>
    {accuracy && <span className="text-purple-500">• {accuracy}%</span>}
  </div>
);

const VerintBrowser = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [tabs, setTabs] = useState([
    { id: 0, title: 'My Dashboard', url: 'verint://dashboard', type: 'dashboard', canClose: false },
    { id: 1, title: 'My Schedule', url: 'verint://wfm-schedule', type: 'wfm', canClose: false },
    { id: 2, title: 'CRM', url: 'verint://crm', type: 'crm-view', canClose: false },
    { id: 3, title: 'Knowledge Base', url: 'https://kb.internal.verint.com', type: 'kb', canClose: true },
    { id: 4, title: 'Google', url: 'https://www.google.com', type: 'google', canClose: true }
  ]);
  const [nextTabId, setNextTabId] = useState(5);
  const [showLeftPanel, setShowLeftPanel] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [llmInput, setLlmInput] = useState('');
  const [llmMessages, setLlmMessages] = useState([
    { role: 'assistant', content: 'Hi! I\'m your Verint AI Assistant. I can help analyze the customer interaction, suggest responses, or answer questions about products and policies.' }
  ]);
  
  const [showRoutingIntelligence, setShowRoutingIntelligence] = useState(true);
  const [showRoutingDetails, setShowRoutingDetails] = useState(false);
  const [showKBArticle, setShowKBArticle] = useState(false);
  const [highlightKBArticle, setHighlightKBArticle] = useState(false);
  const [routingAnalysis, setRoutingAnalysis] = useState({
    compatibilityScore: 94,
    predictedCSAT: 4.5,
    predictedFCR: 87,
    predictedAHT: 8.2,
    routingTime: 198,
    modelsProcessed: 10,
    signalsAnalyzed: 52
  });
  
  const kbArticle = {
    id: 'KB-1847',
    title: 'Enterprise Performance Optimization',
    category: 'Technical Support',
    lastUpdated: '3 days ago',
    relevance: 97,
    views: 1243,
    helpful: 156,
    content: `# Enterprise Performance Optimization Guide

## Overview
This comprehensive guide addresses performance issues in enterprise reporting modules, specifically focusing on database query optimization, caching strategies, and query tuning.

## Common Symptoms
- Report generation taking 5+ minutes
- System slow response during peak hours
- Database timeouts and connection errors
- Memory usage spikes during report runs

## Quick Diagnostic Steps

### 1. Check Database Query Performance
\`\`\`sql
-- Run this query to identify slow queries
SELECT query_text, execution_time, rows_affected
FROM query_log
WHERE execution_time > 5000
ORDER BY execution_time DESC
LIMIT 10;
\`\`\`

### 2. Review Cache Configuration
- Navigate to Settings → Performance → Cache
- Recommended cache size: 2GB minimum
- Enable query result caching
- Set cache TTL to 1 hour for reports

### 3. Optimize Report Queries

**Problem:** Reports using SELECT * instead of specific columns
**Solution:** Specify only needed columns to reduce data transfer`
  };
  
  const openKBArticle = () => {
    const kbTab = tabs.find(t => t.type === 'kb');
    if (kbTab) {
      setActiveTab(kbTab.id);
      setAddressBarUrl(kbTab.url);
      setHighlightKBArticle(true);
      setTimeout(() => setHighlightKBArticle(false), 3000);
    }
  };
  
  const openKBArticleModal = () => {
    setShowKBArticle(true);
  };
  
  const [googleSearchQuery, setGoogleSearchQuery] = useState('enterprise software performance optimization');
  const [addressBarUrl, setAddressBarUrl] = useState('verint://dashboard');
  const [callDuration, setCallDuration] = useState(245);
  
  const [draggedTab, setDraggedTab] = useState(null);
  const [dragOverTab, setDragOverTab] = useState(null);
  
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showCaseModal, setShowCaseModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  
  const [leftPanelTab, setLeftPanelTab] = useState('transcript');
  
  const [emailTo, setEmailTo] = useState('sarah.johnson@techcorp.com');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  
  const [caseTitle, setCaseTitle] = useState('');
  const [casePriority, setCasePriority] = useState('medium');
  const [caseDescription, setCaseDescription] = useState('');
  const [caseCategory, setCaseCategory] = useState('technical');
  
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingTime, setMeetingTime] = useState('');
  const [meetingDuration, setMeetingDuration] = useState('30');
  const [meetingSubject, setMeetingSubject] = useState('');

  const playbook = {
    name: 'Technical Support - Enterprise',
    steps: [
      { id: 1, title: 'Verify Customer Identity', status: 'completed', duration: '0:32' },
      { id: 2, title: 'Identify Issue & Search KB', status: 'active', duration: '1:15' },
      { id: 3, title: 'Review Product Documentation', status: 'pending', duration: null },
      { id: 4, title: 'Provide Resolution', status: 'pending', duration: null },
      { id: 5, title: 'Document & Close', status: 'pending', duration: null }
    ]
  };

  const transcript = [
    { speaker: 'Customer', text: "Hi, I'm having issues with our enterprise software. It's running really slow.", time: '0:15' },
    { speaker: 'Agent', text: "I understand that's frustrating. Let me look into that for you. Can you tell me which module you're experiencing issues with?", time: '0:32' },
    { speaker: 'Customer', text: "It's the reporting module. Takes forever to generate reports.", time: '0:45' },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const addTab = (title, url, type = 'web') => {
    const newTab = { id: nextTabId, title, url, type, canClose: true };
    setTabs([...tabs, newTab]);
    setActiveTab(nextTabId);
    setNextTabId(nextTabId + 1);
    setAddressBarUrl(url);
  };

  const closeTab = (tabId) => {
    const newTabs = tabs.filter(t => t.id !== tabId);
    setTabs(newTabs);
    if (activeTab === tabId && newTabs.length > 0) {
      setActiveTab(newTabs[newTabs.length - 1].id);
      setAddressBarUrl(newTabs[newTabs.length - 1].url);
    }
  };

  const sendLLMMessage = () => {
    if (!llmInput.trim()) return;
    setLlmMessages(prev => [...prev, { role: 'user', content: llmInput }]);
    setLlmInput('');
    setTimeout(() => {
      const responses = [
        "Based on the transcript, I recommend checking the database query optimization settings. The customer mentioned the reporting module is slow, which typically indicates inefficient queries. You can find a detailed guide in KB-1847.",
        "I've analyzed the customer's account - they're on Enterprise Premium tier with a $487K lifetime value. They had a similar issue 45 days ago that was resolved by updating their cache configuration. Would you like me to pull up that previous case?",
        "🧠 Routing Intelligence: You were selected for this interaction with 94% compatibility based on your technical expertise and high empathy scores. The customer is frustrated and needs both technical resolution and emotional support.",
        "I found 3 relevant KB articles about reporting performance. KB-1847 has the highest relevance (97%) and was recently updated. It covers database optimization, caching strategies, and query tuning specifically for the reporting module.",
        "⚠️ Market Context Alert: A product recall was announced 2 hours ago on social media. The customer may be aware and this could be contributing to their frustration. Consider acknowledging this proactively.",
        "📊 Predicted Outcome: With your expertise and approach, we're forecasting 87% FCR probability and 4.5 CSAT for this interaction. The customer's churn risk should decrease from 35% to 15% with successful resolution.",
        "🎯 WFM Alert: Volume surge predicted in 15 minutes. Recommend resolving this interaction efficiently while maintaining quality. Your next break is at 2:30 PM as scheduled."
      ];
      const randomResponse = responses[Math.floor(Math.random() * responses.length)];
      setLlmMessages(prev => [...prev, { 
        role: 'assistant', 
        content: randomResponse
      }]);
    }, 1000);
  };
  
  const sendEmail = () => {
    alert(`Email sent to ${emailTo}\nSubject: ${emailSubject}\n\nEmail logged in CRM.`);
    setShowEmailModal(false);
    setEmailSubject('');
    setEmailBody('');
  };
  
  const sendChatMessage = () => {
    if (!chatInput.trim()) return;
    setChatMessages(prev => [...prev, { role: 'agent', text: chatInput, time: new Date().toLocaleTimeString() }]);
    setChatInput('');
    setTimeout(() => {
      setChatMessages(prev => [...prev, { 
        role: 'customer', 
        text: 'Thanks for reaching out. I appreciate your help with the reporting issue.',
        time: new Date().toLocaleTimeString()
      }]);
    }, 1500);
  };
  
  const startChat = () => {
    setShowChatModal(false);
    setShowLeftPanel(true);
    setLeftPanelTab('chat');
    if (chatMessages.length === 0) {
      setChatMessages([
        { role: 'agent', text: 'Hi Sarah, I\'m here to help with your reporting issue. How can I assist you?', time: new Date().toLocaleTimeString() }
      ]);
      setTimeout(() => {
        setChatMessages(prev => [...prev, { 
          role: 'customer', 
          text: 'Hi! Yes, the reporting module is still running very slow. It takes several minutes to generate even simple reports.',
          time: new Date().toLocaleTimeString()
        }]);
      }, 2000);
    }
  };
  
  const createCase = () => {
    alert(`Case created!\nTitle: ${caseTitle}\nPriority: ${casePriority}\nCategory: ${caseCategory}\n\nCase has been assigned to you.`);
    setShowCaseModal(false);
    setCaseTitle('');
    setCaseDescription('');
  };
  
  const scheduleMeeting = () => {
    alert(`Meeting scheduled!\nDate: ${meetingDate}\nTime: ${meetingTime}\nDuration: ${meetingDuration} minutes\n\nCalendar invite sent to sarah.johnson@techcorp.com`);
    setShowMeetingModal(false);
    setMeetingDate('');
    setMeetingTime('');
    setMeetingSubject('');
  };

  const handleDragStart = (e, tabId) => {
    setDraggedTab(tabId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, tabId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedTab !== tabId) {
      setDragOverTab(tabId);
    }
  };

  const handleDragEnd = () => {
    if (draggedTab !== null && dragOverTab !== null && draggedTab !== dragOverTab) {
      const draggedIndex = tabs.findIndex(t => t.id === draggedTab);
      const targetIndex = tabs.findIndex(t => t.id === dragOverTab);
      
      const newTabs = [...tabs];
      const [removed] = newTabs.splice(draggedIndex, 1);
      newTabs.splice(targetIndex, 0, removed);
      
      setTabs(newTabs);
    }
    setDraggedTab(null);
    setDragOverTab(null);
  };

  const renderTabContent = (tab) => {
    if (tab.type === 'dashboard') {
      return (
        <div className="flex-1 bg-gradient-to-br from-gray-50 to-blue-50 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Contact Center Dashboard</h1>
            <p className="text-gray-600 mb-6">Real-time performance insights for John Smith</p>
            
            {/* WFM Forecast Alert */}
            <Tooltip content={
              <div>
                <div className="font-bold mb-2">🤖 ML Models Active:</div>
                <div className="space-y-1 mb-2">
                  <div>• <strong>Demand Forecasting Model</strong> (91% accuracy)</div>
                  <div className="text-xs ml-4">Predicts +40% call volume surge in 15 minutes</div>
                  <div>• <strong>Dynamic Priority Model</strong> (89% accuracy)</div>
                  <div className="text-xs ml-4">Adjusts routing to pre-position agents</div>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-700">
                  <div className="font-bold mb-1">📊 Data Sources:</div>
                  <div className="text-xs">• Workforce Management API (historical volumes, schedules)</div>
                  <div className="text-xs">• Contact Center Statistics API (real-time patterns)</div>
                  <div className="text-xs">• External: Weather data, promotional calendar, economic indicators</div>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-700">
                  <div className="font-bold mb-1">💡 Use Case:</div>
                  <div className="text-xs"><strong>Predictive Staffing Intelligence</strong> - ML-powered forecasting that combines historical data with external signals for proactive capacity planning</div>
                </div>
              </div>
            }>
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg shadow-lg p-4 mb-6 cursor-help hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                      <TrendingUp className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="font-bold text-lg flex items-center gap-2">
                        Forecast-Driven Routing Active
                        <Info className="w-4 h-4" />
                      </div>
                      <div className="text-sm text-blue-100">Volume surge predicted in 15 minutes (+40%). System routing conservatively to pre-position agents.</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">198ms</div>
                    <div className="text-xs text-blue-200">Avg routing decision</div>
                  </div>
                </div>
              </div>
            </Tooltip>
            
            {/* Key Metrics Cards */}
            <div className="grid grid-cols-5 gap-4 mb-6">
              <Tooltip content={
                <div>
                  <div className="font-bold mb-2">📊 Data Sources:</div>
                  <div className="text-xs mb-1">• <strong>Contact Center Statistics API</strong> - Real-time interaction counts across all channels</div>
                  <div className="text-xs">• <strong>Workforce Analytics API</strong> - Historical trends and productivity metrics</div>
                  <div className="mt-2 pt-2 border-t border-gray-700">
                    <div className="font-bold mb-1">💡 Use Case:</div>
                    <div className="text-xs"><strong>Embedded Agent Performance Dashboard</strong> - Real-time metrics embedded directly in your workspace without switching applications</div>
                    <div className="text-xs mt-1 text-green-300">✓ 30% reduction in supervisor context-switching time</div>
                  </div>
                </div>
              }>
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-lg shadow-lg p-4 cursor-help hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <Activity className="w-8 h-8 opacity-80" />
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <div className="text-3xl font-bold mb-1">23</div>
                  <div className="text-sm opacity-90">Interactions Today</div>
                  <div className="text-xs mt-2 opacity-75">↑ 12% vs yesterday</div>
                </div>
              </Tooltip>
              
              <Tooltip content={
                <div>
                  <div className="font-bold mb-2">🤖 ML Model:</div>
                  <div className="mb-1"><strong>Interaction Outcome Prediction</strong> (87% accuracy)</div>
                  <div className="text-xs mb-2">Predicts first call resolution based on customer context, agent capabilities, issue complexity, and historical patterns</div>
                  <div className="mt-2 pt-2 border-t border-gray-700">
                    <div className="font-bold mb-1">📊 Data Sources:</div>
                    <div className="text-xs">• <strong>Interaction Analytics API</strong> - Issue complexity, customer history</div>
                    <div className="text-xs">• <strong>Quality Management API</strong> - Historical resolution outcomes</div>
                    <div className="text-xs">• <strong>Agent Profile</strong> - Skills, performance history</div>
                  </div>
                </div>
              }>
                <div className="bg-gradient-to-br from-green-600 to-green-700 text-white rounded-lg shadow-lg p-4 cursor-help hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <CheckCircle className="w-8 h-8 opacity-80" />
                    <Award className="w-5 h-5" />
                  </div>
                  <div className="text-3xl font-bold mb-1">87%</div>
                  <div className="text-sm opacity-90">First Call Resolution</div>
                  <div className="text-xs mt-2 opacity-75">Target: 85% ✓</div>
                </div>
              </Tooltip>
              
              <Tooltip content={
                <div>
                  <div className="font-bold mb-2">📊 Data Sources:</div>
                  <div className="text-xs mb-1">• <strong>Interaction Analytics API</strong> - AHT by interaction type</div>
                  <div className="text-xs mb-1">• <strong>Workforce Analytics API</strong> - Agent productivity metrics</div>
                  <div className="text-xs">• <strong>Digital Experience API</strong> - Desktop performance correlation</div>
                  <div className="mt-2 pt-2 border-t border-gray-700">
                    <div className="font-bold mb-1">💡 Insight:</div>
                    <div className="text-xs">Desktop experience issues add avg 45 seconds to AHT. Your desktop performance score: 94% (excellent)</div>
                  </div>
                </div>
              }>
                <div className="bg-gradient-to-br from-purple-600 to-purple-700 text-white rounded-lg shadow-lg p-4 cursor-help hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <Clock className="w-8 h-8 opacity-80" />
                    <Target className="w-5 h-5" />
                  </div>
                  <div className="text-3xl font-bold mb-1">5:42</div>
                  <div className="text-sm opacity-90">Avg Handle Time</div>
                  <div className="text-xs mt-2 opacity-75">Target: 6:00 ✓</div>
                </div>
              </Tooltip>
              
              <Tooltip content={
                <div>
                  <div className="font-bold mb-2">🤖 ML Model:</div>
                  <div className="mb-1"><strong>Sentiment Trajectory Forecasting</strong> (86% accuracy)</div>
                  <div className="text-xs mb-2">Predicts customer satisfaction in real-time by analyzing speech patterns, tone, word choice, and interaction flow</div>
                  <div className="mt-2 pt-2 border-t border-gray-700">
                    <div className="font-bold mb-1">📊 Data Sources:</div>
                    <div className="text-xs">• <strong>Speech Analytics API</strong> - Real-time sentiment, tone analysis</div>
                    <div className="text-xs">• <strong>Quality Management API</strong> - Post-interaction CSAT surveys</div>
                    <div className="text-xs">• <strong>Customer Profile</strong> - Historical satisfaction trends</div>
                  </div>
                </div>
              }>
                <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white rounded-lg shadow-lg p-4 cursor-help hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <Star className="w-8 h-8 opacity-80 fill-current" />
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <div className="text-3xl font-bold mb-1">4.8</div>
                  <div className="text-sm opacity-90">CSAT Score</div>
                  <div className="text-xs mt-2 opacity-75">↑ 0.3 this week</div>
                </div>
              </Tooltip>
              
              <Tooltip content={
                <div>
                  <div className="font-bold mb-2">🤖 ML Model:</div>
                  <div className="mb-1"><strong>Agent Performance Prediction</strong> (88% accuracy)</div>
                  <div className="text-xs mb-2">Multi-dimensional scoring across productivity, quality, customer outcomes, coaching effectiveness, and skill development</div>
                  <div className="mt-2 pt-2 border-t border-gray-700">
                    <div className="font-bold mb-1">📊 Data Sources:</div>
                    <div className="text-xs">• <strong>Quality Management API</strong> - QA evaluation scores</div>
                    <div className="text-xs">• <strong>Workforce Analytics API</strong> - Productivity and adherence</div>
                    <div className="text-xs">• <strong>Interaction Outcomes</strong> - FCR, CSAT, resolution time</div>
                    <div className="text-xs">• <strong>Learning Management</strong> - Training completion, certifications</div>
                  </div>
                </div>
              }>
                <div className="bg-gradient-to-br from-orange-600 to-orange-700 text-white rounded-lg shadow-lg p-4 cursor-help hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <TrendingUp className="w-8 h-8 opacity-80" />
                    <div className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded">#3</div>
                  </div>
                  <div className="text-3xl font-bold mb-1">94%</div>
                  <div className="text-sm opacity-90">Quality Score</div>
                  <div className="text-xs mt-2 opacity-75">Rank #3 of 12 teams</div>
                </div>
              </Tooltip>
            </div>

            {/* My Performance vs Team */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow-md p-5 border border-gray-200">
                <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2 text-blue-600" />
                  My Performance Today
                </h2>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Calls Handled</span>
                      <span className="font-semibold text-gray-900">23 / 25 goal</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{width: '92%'}}></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Schedule Adherence</span>
                      <span className="font-semibold text-green-600">98%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full" style={{width: '98%'}}></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">Occupancy Rate</span>
                      <span className="font-semibold text-purple-600">85%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-purple-600 h-2 rounded-full" style={{width: '85%'}}></div>
                    </div>
                  </div>
                  
                  <div className="pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Current State</span>
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></div>
                        <span className="text-sm font-semibold text-gray-900">On Call - {formatTime(callDuration)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-5 border border-gray-200">
                <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <Users className="w-5 h-5 mr-2 text-teal-600" />
                  Technical Support Team (Team Alpha)
                </h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 bg-blue-50 rounded">
                      <div className="text-2xl font-bold text-blue-600">156</div>
                      <div className="text-xs text-gray-600 mt-1">Calls Today</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded">
                      <div className="text-2xl font-bold text-green-600">89%</div>
                      <div className="text-xs text-gray-600 mt-1">Team FCR</div>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded">
                      <div className="text-2xl font-bold text-purple-600">5:38</div>
                      <div className="text-xs text-gray-600 mt-1">Team AHT</div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Team Members Active</span>
                      <span className="font-semibold text-gray-900">8 / 12</span>
                    </div>
                    <div className="flex space-x-1">
                      {[...Array(8)].map((_, i) => (
                        <div key={i} className="flex-1 h-2 bg-green-500 rounded"></div>
                      ))}
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="flex-1 h-2 bg-gray-300 rounded"></div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-sm text-gray-600 mb-2">Top Performers Today</div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-700">1. Emma Wilson</span>
                        <span className="font-semibold text-green-600">28 calls • 92% FCR</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-700">2. Michael Chen</span>
                        <span className="font-semibold text-green-600">26 calls • 88% FCR</span>
                      </div>
                      <div className="flex justify-between text-xs bg-blue-50 px-2 py-1 rounded">
                        <span className="text-gray-700 font-semibold">3. John Smith (You)</span>
                        <span className="font-semibold text-blue-600">23 calls • 87% FCR</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Queue Performance */}
            <div className="bg-white rounded-lg shadow-md p-5 border border-gray-200 mb-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <Headphones className="w-5 h-5 mr-2 text-purple-600" />
                My Assigned Queues
              </h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-white rounded-lg border border-green-200">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">Technical Support - Enterprise</div>
                    <div className="text-xs text-gray-600 mt-1">Priority Queue</div>
                  </div>
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <div className="text-xs text-gray-600">Waiting</div>
                      <div className="text-lg font-bold text-orange-600">3</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-600">Longest Wait</div>
                      <div className="text-lg font-bold text-gray-900">2:15</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-600">SLA</div>
                      <div className="text-lg font-bold text-green-600">94%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-600">My Calls</div>
                      <div className="text-lg font-bold text-blue-600">18</div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">General Support</div>
                    <div className="text-xs text-gray-600 mt-1">Secondary Queue</div>
                  </div>
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <div className="text-xs text-gray-600">Waiting</div>
                      <div className="text-lg font-bold text-orange-600">8</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-600">Longest Wait</div>
                      <div className="text-lg font-bold text-gray-900">4:32</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-600">SLA</div>
                      <div className="text-lg font-bold text-yellow-600">87%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-600">My Calls</div>
                      <div className="text-lg font-bold text-blue-600">5</div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">Billing & Accounts</div>
                    <div className="text-xs text-gray-600 mt-1">Overflow Queue</div>
                  </div>
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <div className="text-xs text-gray-600">Waiting</div>
                      <div className="text-lg font-bold text-green-600">1</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-600">Longest Wait</div>
                      <div className="text-lg font-bold text-gray-900">0:45</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-600">SLA</div>
                      <div className="text-lg font-bold text-green-600">96%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-600">My Calls</div>
                      <div className="text-lg font-bold text-blue-600">0</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Team Comparison */}
            <div className="bg-white rounded-lg shadow-md p-5 border border-gray-200">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-orange-600" />
                Team Performance Rankings - All Queues
              </h2>
              <div className="space-y-3">
                <div className="flex items-center p-3 bg-gradient-to-r from-yellow-50 to-white rounded-lg border border-yellow-300">
                  <div className="flex items-center justify-center w-8 h-8 bg-yellow-400 text-white font-bold rounded mr-3">1</div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">Team Bravo - Sales Support</div>
                    <div className="text-xs text-gray-600">156 calls • 92% FCR • 4:45 AHT • 4.9 CSAT</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-green-600">96%</div>
                    <div className="text-xs text-gray-600">Overall Score</div>
                  </div>
                </div>
                
                <div className="flex items-center p-3 bg-gradient-to-r from-gray-100 to-white rounded-lg border border-gray-300">
                  <div className="flex items-center justify-center w-8 h-8 bg-gray-400 text-white font-bold rounded mr-3">2</div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">Team Delta - Product Support</div>
                    <div className="text-xs text-gray-600">142 calls • 91% FCR • 5:12 AHT • 4.8 CSAT</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-green-600">95%</div>
                    <div className="text-xs text-gray-600">Overall Score</div>
                  </div>
                </div>
                
                <div className="flex items-center p-3 bg-gradient-to-r from-blue-100 to-white rounded-lg border-2 border-blue-400 shadow-md">
                  <div className="flex items-center justify-center w-8 h-8 bg-orange-500 text-white font-bold rounded mr-3">3</div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">Team Alpha - Technical Support (YOUR TEAM)</div>
                    <div className="text-xs text-gray-600">156 calls • 89% FCR • 5:38 AHT • 4.7 CSAT</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-blue-600">94%</div>
                    <div className="text-xs text-gray-600">Overall Score</div>
                  </div>
                  <div className="ml-3">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                
                <div className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-center w-8 h-8 bg-gray-300 text-white font-bold rounded mr-3">4</div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">Team Charlie - Customer Care</div>
                    <div className="text-xs text-gray-600">178 calls • 88% FCR • 5:55 AHT • 4.6 CSAT</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-600">92%</div>
                    <div className="text-xs text-gray-600">Overall Score</div>
                  </div>
                </div>
                
                <div className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-center w-8 h-8 bg-gray-300 text-white font-bold rounded mr-3">5</div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">Team Echo - Billing Support</div>
                    <div className="text-xs text-gray-600">134 calls • 86% FCR • 6:12 AHT • 4.5 CSAT</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-600">91%</div>
                    <div className="text-xs text-gray-600">Overall Score</div>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start">
                  <TrendingUp className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-gray-800 mb-1">Performance Insight</div>
                    <div className="text-xs text-gray-700">Your team moved up 2 positions this week! Focus on reducing AHT by 15 seconds to potentially reach #2.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (tab.type === 'wfm') {
      const currentHour = 14;
      const currentMinute = 20;
      
      const scheduleBlocks = [
        { start: '08:00', end: '10:00', type: 'queue', queue: 'Technical Support - Enterprise', team: 'Team Alpha', color: 'blue', activity: 'Handling Calls', skills: ['Voice', 'Chat'] },
        { start: '10:00', end: '10:15', type: 'break', activity: 'Morning Break', color: 'yellow' },
        { start: '10:15', end: '12:00', type: 'queue', queue: 'Technical Support - Enterprise', team: 'Team Alpha', color: 'blue', activity: 'Handling Calls', skills: ['Voice', 'Chat', 'Email'] },
        { start: '12:00', end: '12:45', type: 'lunch', activity: 'Lunch Break', color: 'orange' },
        { start: '12:45', end: '14:30', type: 'queue', queue: 'General Support', team: 'Team Alpha', color: 'green', activity: 'Handling Calls', skills: ['Voice', 'Chat'] },
        { start: '14:30', end: '14:45', type: 'break', activity: 'Afternoon Break', color: 'yellow' },
        { start: '14:45', end: '16:00', type: 'queue', queue: 'Technical Support - Enterprise', team: 'Team Alpha', color: 'blue', activity: 'Handling Calls', skills: ['Voice', 'Chat'] },
        { start: '16:00', end: '16:30', type: 'meeting', activity: 'Team Huddle', team: 'Team Alpha', color: 'purple' },
        { start: '16:30', end: '17:00', type: 'training', activity: 'Product Training', color: 'teal' },
      ];
      
      const weekSchedule = [
        { day: 'Monday', date: 'Oct 27', shift: '8:00 AM - 5:00 PM', queues: ['Tech Support', 'General'], hours: 8.5, status: 'today' },
        { day: 'Tuesday', date: 'Oct 28', shift: '8:00 AM - 5:00 PM', queues: ['Tech Support', 'Billing'], hours: 8.5, status: 'upcoming' },
        { day: 'Wednesday', date: 'Oct 29', shift: '9:00 AM - 6:00 PM', queues: ['Tech Support'], hours: 8.5, status: 'upcoming' },
        { day: 'Thursday', date: 'Oct 30', shift: '8:00 AM - 5:00 PM', queues: ['Tech Support', 'General'], hours: 8.5, status: 'upcoming' },
        { day: 'Friday', date: 'Oct 31', shift: '8:00 AM - 4:00 PM', queues: ['Tech Support'], hours: 7.5, status: 'upcoming' },
      ];
      
      const isCurrentBlock = (startTime, endTime) => {
        const [startHour, startMin] = startTime.split(':').map(Number);
        const [endHour, endMin] = endTime.split(':').map(Number);
        const currentTimeMinutes = currentHour * 60 + currentMinute;
        const startTimeMinutes = startHour * 60 + startMin;
        const endTimeMinutes = endHour * 60 + endMin;
        return currentTimeMinutes >= startTimeMinutes && currentTimeMinutes < endTimeMinutes;
      };
      
      return (
        <div className="flex-1 bg-gradient-to-br from-gray-50 to-purple-50 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">My Schedule</h1>
                <p className="text-gray-600">Workforce Management - Schedule & Queue Assignments</p>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <div className="text-xs text-gray-600">Scheduled Hours</div>
                    <div className="text-2xl font-bold text-blue-600">40.5</div>
                    <div className="text-xs text-gray-500">this week</div>
                  </div>
                  <div className="h-12 w-px bg-gray-300"></div>
                  <div className="text-center">
                    <div className="text-xs text-gray-600">Adherence</div>
                    <div className="text-2xl font-bold text-green-600">98%</div>
                    <div className="text-xs text-gray-500">today</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Today's Schedule */}
            <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-blue-600" />
                  Today's Schedule - Monday, October 27, 2025
                </h2>
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-3 h-3 bg-red-500 rounded animate-pulse"></div>
                  <span className="font-semibold text-gray-700">Currently: Handling Calls - General Support (2:20 PM)</span>
                </div>
              </div>
              
              <div className="space-y-2">
                {scheduleBlocks.map((block, idx) => {
                  const isCurrent = isCurrentBlock(block.start, block.end);
                  const bgColors = {
                    queue: 'from-blue-50 to-blue-100',
                    break: 'from-yellow-50 to-yellow-100',
                    lunch: 'from-orange-50 to-orange-100',
                    meeting: 'from-purple-50 to-purple-100',
                    training: 'from-teal-50 to-teal-100'
                  };
                  const borderColors = {
                    queue: 'border-blue-300',
                    break: 'border-yellow-300',
                    lunch: 'border-orange-300',
                    meeting: 'border-purple-300',
                    training: 'border-teal-300'
                  };
                  const iconColors = {
                    queue: 'text-blue-600',
                    break: 'text-yellow-600',
                    lunch: 'text-orange-600',
                    meeting: 'text-purple-600',
                    training: 'text-teal-600'
                  };
                  
                  const isGeneralSupportBlock = block.queue === 'General Support';
                  
                  return (
                    <div 
                      key={idx} 
                      className={`flex items-center p-4 rounded-lg border-2 bg-gradient-to-r ${
                        isGeneralSupportBlock ? 'from-green-50 to-green-100 border-green-300' : `${bgColors[block.type]} ${borderColors[block.type]}`
                      } ${
                        isCurrent ? 'ring-4 ring-red-400 ring-opacity-50 shadow-lg scale-105' : 'shadow-sm'
                      } transition-all duration-300`}
                    >
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="text-center min-w-[100px]">
                          <div className={`text-lg font-bold ${isGeneralSupportBlock ? 'text-green-600' : iconColors[block.type]}`}>{block.start}</div>
                          <div className="text-xs text-gray-600">to</div>
                          <div className={`text-lg font-bold ${isGeneralSupportBlock ? 'text-green-600' : iconColors[block.type]}`}>{block.end}</div>
                        </div>
                        
                        <div className="h-16 w-px bg-gray-300"></div>
                        
                        <div className="flex-1">
                          <div className="flex items-center mb-1">
                            {block.type === 'queue' && <Headphones className={`w-5 h-5 mr-2 ${isGeneralSupportBlock ? 'text-green-600' : iconColors[block.type]}`} />}
                            {block.type === 'break' && <Clock className={`w-5 h-5 mr-2 ${iconColors[block.type]}`} />}
                            {block.type === 'lunch' && <Clock className={`w-5 h-5 mr-2 ${iconColors[block.type]}`} />}
                            {block.type === 'meeting' && <Users className={`w-5 h-5 mr-2 ${iconColors[block.type]}`} />}
                            {block.type === 'training' && <BookOpen className={`w-5 h-5 mr-2 ${iconColors[block.type]}`} />}
                            <div className="font-bold text-gray-900 text-lg">{block.activity}</div>
                            {isCurrent && (
                              <div className="ml-2 px-2 py-1 bg-red-500 text-white text-xs rounded-full animate-pulse flex items-center">
                                <Phone className="w-3 h-3 mr-1" />
                                ACTIVE CALL - IN PROGRESS
                              </div>
                            )}
                          </div>
                          
                          {block.queue && (
                            <div className="text-sm text-gray-700 mb-1">
                              <span className="font-semibold">Queue:</span> {block.queue}
                            </div>
                          )}
                          
                          {block.team && (
                            <div className="text-sm text-gray-700 mb-1">
                              <span className="font-semibold">Team:</span> {block.team}
                            </div>
                          )}
                          
                          {block.skills && (
                            <div className="flex items-center space-x-2 mt-2">
                              <span className="text-xs text-gray-600">Channels:</span>
                              {block.skills.map((skill, i) => (
                                <span key={i} className="px-2 py-1 bg-white bg-opacity-60 text-gray-700 text-xs rounded border border-gray-300 font-medium">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div className="text-right">
                          <div className="text-sm text-gray-600">Duration</div>
                          <div className="text-xl font-bold text-gray-900">
                            {(() => {
                              const [startH, startM] = block.start.split(':').map(Number);
                              const [endH, endM] = block.end.split(':').map(Number);
                              const minutes = (endH * 60 + endM) - (startH * 60 + startM);
                              const hours = Math.floor(minutes / 60);
                              const mins = minutes % 60;
                              return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start">
                  <Activity className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-gray-800 mb-1">Schedule Summary</div>
                    <div className="text-sm text-gray-700 space-y-1">
                      <div>• <strong>Currently Active:</strong> General Support queue (2:20 PM - Handling voice call)</div>
                      <div>• <strong>Primary Queue:</strong> Technical Support - Enterprise (6.5 hours total)</div>
                      <div>• <strong>Secondary Queue:</strong> General Support (1.75 hours total)</div>
                      <div>• <strong>Total Break Time:</strong> 1 hour (3 breaks)</div>
                      <div>• <strong>Team Activities:</strong> 1 meeting, 1 training session</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Week Overview */}
            <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200 mb-6">
              <h2 className="text-xl font-bold text-gray-800 flex items-center mb-4">
                <BarChart3 className="w-5 h-5 mr-2 text-purple-600" />
                This Week's Schedule Overview
              </h2>
              
              <div className="space-y-3">
                {weekSchedule.map((day, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center p-4 rounded-lg border-2 ${
                      day.status === 'today' 
                        ? 'bg-gradient-to-r from-blue-100 to-blue-50 border-blue-400 shadow-md' 
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center space-x-6 flex-1">
                      <div className="text-center min-w-[120px]">
                        <div className={`text-lg font-bold ${day.status === 'today' ? 'text-blue-700' : 'text-gray-700'}`}>
                          {day.day}
                        </div>
                        <div className="text-sm text-gray-600">{day.date}</div>
                        {day.status === 'today' && (
                          <div className="mt-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full inline-block">
                            TODAY
                          </div>
                        )}
                      </div>
                      
                      <div className="h-12 w-px bg-gray-300"></div>
                      
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <Clock className="w-4 h-4 mr-2 text-gray-600" />
                          <span className="font-semibold text-gray-800">{day.shift}</span>
                          <span className="ml-3 text-sm text-gray-600">({day.hours} hours)</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Headphones className="w-4 h-4 text-gray-600" />
                          <span className="text-sm text-gray-600">Queues:</span>
                          {day.queues.map((queue, i) => (
                            <span key={i} className="px-2 py-0.5 bg-white text-gray-700 text-xs rounded border border-gray-300">
                              {queue}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div className="text-right">
                        {day.status === 'today' ? (
                          <div className="text-green-600 font-semibold text-sm flex items-center">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            In Progress
                          </div>
                        ) : (
                          <div className="text-gray-500 text-sm">Scheduled</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-center">
                  <div className="text-2xl font-bold text-purple-600">40.5</div>
                  <div className="text-xs text-gray-600 mt-1">Total Hours</div>
                </div>
                <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
                  <div className="text-2xl font-bold text-green-600">5</div>
                  <div className="text-xs text-gray-600 mt-1">Work Days</div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
                  <div className="text-2xl font-bold text-blue-600">3</div>
                  <div className="text-xs text-gray-600 mt-1">Queues Assigned</div>
                </div>
              </div>
            </div>

            {/* Queue Assignments & Skills */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                <h2 className="text-lg font-bold text-gray-800 flex items-center mb-4">
                  <Target className="w-5 h-5 mr-2 text-orange-600" />
                  My Queue Assignments
                </h2>
                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-gray-800">Technical Support - Enterprise</div>
                      <div className="px-2 py-1 bg-blue-600 text-white text-xs rounded">Primary</div>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>• Priority: High</div>
                      <div>• Weekly Hours: ~28 hours</div>
                      <div>• Skills Required: Technical, Product Knowledge</div>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-gray-800">General Support</div>
                      <div className="px-2 py-1 bg-green-600 text-white text-xs rounded">Secondary</div>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>• Priority: Medium</div>
                      <div>• Weekly Hours: ~8 hours</div>
                      <div>• Skills Required: General Support</div>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-gray-800">Billing & Accounts</div>
                      <div className="px-2 py-1 bg-purple-600 text-white text-xs rounded">Overflow</div>
                    </div>
                    <div className="text-sm text-gray-600 space-y-1">
                      <div>• Priority: Low</div>
                      <div>• Weekly Hours: ~4 hours</div>
                      <div>• Skills Required: Billing Knowledge</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                <h2 className="text-lg font-bold text-gray-800 flex items-center mb-4">
                  <Award className="w-5 h-5 mr-2 text-teal-600" />
                  My Skills & Channels
                </h2>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-700 mb-2">Certified Channels</div>
                    <div className="flex flex-wrap gap-2">
                      <div className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg border border-blue-300 font-medium text-sm">
                        📞 Voice
                      </div>
                      <div className="px-3 py-2 bg-green-100 text-green-700 rounded-lg border border-green-300 font-medium text-sm">
                        💬 Chat
                      </div>
                      <div className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg border border-purple-300 font-medium text-sm">
                        ✉️ Email
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Product Expertise</div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Enterprise Software Suite</span>
                        <div className="flex items-center">
                          <div className="w-24 h-2 bg-gray-200 rounded-full mr-2">
                            <div className="w-[95%] h-2 bg-green-500 rounded-full"></div>
                          </div>
                          <span className="text-xs font-semibold text-gray-700">95%</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Database Management</span>
                        <div className="flex items-center">
                          <div className="w-24 h-2 bg-gray-200 rounded-full mr-2">
                            <div className="w-[88%] h-2 bg-blue-500 rounded-full"></div>
                          </div>
                          <span className="text-xs font-semibold text-gray-700">88%</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Reporting Tools</span>
                        <div className="flex items-center">
                          <div className="w-24 h-2 bg-gray-200 rounded-full mr-2">
                            <div className="w-[92%] h-2 bg-teal-500 rounded-full"></div>
                          </div>
                          <span className="text-xs font-semibold text-gray-700">92%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Team Assignments</div>
                    <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                      <div className="font-semibold text-blue-900">Team Alpha - Technical Support</div>
                      <div className="text-xs text-gray-600 mt-1">Supervisor: Sarah Martinez</div>
                      <div className="text-xs text-gray-600">Team Members: 12 agents</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (tab.type === 'crm-view') {
      return (
        <div className="flex-1 bg-gray-50 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto">
            {/* Customer Header */}
            <Tooltip content={
              <div>
                <div className="font-bold mb-2">💡 Use Case:</div>
                <div><strong>Customer Journey Analytics with Workforce Context</strong></div>
                <div className="text-xs mt-1 mb-2">Unified view combining CRM customer data with real-time agent context and interaction history</div>
                <div className="mt-2 pt-2 border-t border-gray-700">
                  <div className="font-bold mb-1">🤖 ML Models Active:</div>
                  <div className="text-xs">• <strong>Churn Risk Scoring</strong> (89% accuracy) - Predicts 35% churn probability</div>
                  <div className="text-xs">• <strong>Lifetime Value Forecasting</strong> (92% accuracy) - $487K LTV projection</div>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-700">
                  <div className="font-bold mb-1">📊 Data Sources:</div>
                  <div className="text-xs">• Customer Profile (CDP/CRM integration)</div>
                  <div className="text-xs">• Interaction Analytics API (history across channels)</div>
                  <div className="text-xs">• Quality Management API (agent performance on past interactions)</div>
                  <div className="text-xs">• Speech/Text Analytics (sentiment trends)</div>
                </div>
              </div>
            }>
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg shadow-lg p-6 mb-6 cursor-help hover:shadow-xl transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-3xl font-bold backdrop-blur-sm">
                      SJ
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold mb-1 flex items-center gap-2">
                        Sarah Johnson
                        <Info className="w-5 h-5" />
                      </h1>
                      <p className="text-purple-200 mb-3">Senior IT Manager • TechCorp Solutions</p>
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 mr-1" />
                          sarah.johnson@techcorp.com
                        </div>
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 mr-1" />
                          +1 (555) 123-4567
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="px-4 py-2 bg-yellow-400 text-purple-900 rounded-lg font-bold text-lg mb-2">
                      ENTERPRISE PREMIUM
                    </div>
                    <div className="text-sm text-purple-200">Customer Since: Jan 2019</div>
                    <div className="text-sm text-purple-200">Account: ACC-94821</div>
                  </div>
                </div>
              </div>
            </Tooltip>

            {/* Key Metrics */}
            <div className="grid grid-cols-5 gap-4 mb-6">
              <Tooltip content={
                <div>
                  <div className="font-bold mb-2">🤖 ML Model:</div>
                  <div className="mb-1"><strong>Lifetime Value Forecasting</strong> (92% accuracy)</div>
                  <div className="text-xs">Predicts customer revenue potential based on purchase history, engagement patterns, product usage, and industry benchmarks</div>
                </div>
              }>
                <div className="bg-white rounded-lg shadow p-4 border border-gray-200 cursor-help hover:shadow-lg transition-shadow">
                  <div className="text-xs text-gray-600 mb-1">Lifetime Value</div>
                  <div className="text-2xl font-bold text-green-600">$487K</div>
                  <div className="text-xs text-gray-500 mt-1">↑ 12% YoY</div>
                </div>
              </Tooltip>
              
              <Tooltip content={
                <div>
                  <div className="font-bold mb-2">📊 Data Source:</div>
                  <div className="text-xs mb-1"><strong>Interaction Analytics API</strong></div>
                  <div className="text-xs">Aggregates all touchpoints across phone, email, chat, web self-service, and social channels</div>
                </div>
              }>
                <div className="bg-white rounded-lg shadow p-4 border border-gray-200 cursor-help hover:shadow-lg transition-shadow">
                  <div className="text-xs text-gray-600 mb-1">Total Interactions</div>
                  <div className="text-2xl font-bold text-blue-600">142</div>
                  <div className="text-xs text-gray-500 mt-1">Last 12 months</div>
                </div>
              </Tooltip>
              
              <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
                <div className="text-xs text-gray-600 mb-1">Open Cases</div>
                <div className="text-2xl font-bold text-orange-600">3</div>
                <div className="text-xs text-gray-500 mt-1">1 high priority</div>
              </div>
              
              <Tooltip content={
                <div>
                  <div className="font-bold mb-2">🤖 ML Model:</div>
                  <div className="mb-1"><strong>Sentiment Trajectory Forecasting</strong> (86% accuracy)</div>
                  <div className="text-xs mb-2">Aggregated sentiment analysis from Speech/Text Analytics across all customer interactions</div>
                  <div className="mt-2 pt-2 border-t border-gray-700">
                    <div className="text-xs">Historical CSAT: 4.7/5.0 (above average)</div>
                  </div>
                </div>
              }>
                <div className="bg-white rounded-lg shadow p-4 border border-gray-200 cursor-help hover:shadow-lg transition-shadow">
                  <div className="text-xs text-gray-600 mb-1">CSAT Score</div>
                  <div className="text-2xl font-bold text-purple-600">4.7</div>
                  <div className="text-xs text-gray-500 mt-1">Above average</div>
                </div>
              </Tooltip>
              
              <Tooltip content={
                <div>
                  <div className="font-bold mb-2">🤖 ML Model:</div>
                  <div className="mb-1"><strong>Customer Health Score</strong> (composite metric)</div>
                  <div className="text-xs">Combines usage patterns, support ticket trends, payment history, engagement level, and sentiment analysis into single health indicator</div>
                </div>
              }>
                <div className="bg-white rounded-lg shadow p-4 border border-gray-200 cursor-help hover:shadow-lg transition-shadow">
                  <div className="text-xs text-gray-600 mb-1">Health Score</div>
                  <div className="text-2xl font-bold text-teal-600">85%</div>
                  <div className="text-xs text-gray-500 mt-1">Good standing</div>
                </div>
              </Tooltip>
            </div>

            <div className="grid grid-cols-3 gap-6 mb-6">
              {/* Contact Information */}
              <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
                <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2 text-blue-600" />
                  Contact Information
                </h2>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-gray-600">Full Name</div>
                    <div className="text-sm font-semibold text-gray-900">Sarah Johnson</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600">Title</div>
                    <div className="text-sm font-semibold text-gray-900">Senior IT Manager</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600">Department</div>
                    <div className="text-sm font-semibold text-gray-900">Information Technology</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600">Company</div>
                    <div className="text-sm font-semibold text-gray-900">TechCorp Solutions</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600">Location</div>
                    <div className="text-sm font-semibold text-gray-900">San Francisco, CA</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600">Preferred Contact Method</div>
                    <div className="text-sm font-semibold text-gray-900">Email, Phone</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600">Timezone</div>
                    <div className="text-sm font-semibold text-gray-900">Pacific Time (PST)</div>
                  </div>
                </div>
              </div>

              {/* Account Details */}
              <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
                <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2 text-purple-600" />
                  Account Details
                </h2>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-gray-600">Account ID</div>
                    <div className="text-sm font-semibold text-gray-900">ACC-94821</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600">Account Type</div>
                    <div className="inline-block px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded">
                      Enterprise Premium
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600">Contract Status</div>
                    <div className="inline-block px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                      Active
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600">Contract End Date</div>
                    <div className="text-sm font-semibold text-gray-900">Dec 31, 2025</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600">Annual Revenue</div>
                    <div className="text-sm font-semibold text-green-600">$125,000</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600">Payment Terms</div>
                    <div className="text-sm font-semibold text-gray-900">Net 30</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600">Account Manager</div>
                    <div className="text-sm font-semibold text-gray-900">Jennifer Martinez</div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
                <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <Zap className="w-5 h-5 mr-2 text-orange-600" />
                  Quick Actions
                </h2>
                <div className="space-y-2">
                  <button 
                    onClick={() => setShowCallModal(true)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium flex items-center justify-center"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Call Customer
                  </button>
                  <button 
                    onClick={() => setShowEmailModal(true)}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium flex items-center justify-center"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </button>
                  <button 
                    onClick={startChat}
                    className="w-full px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 text-sm font-medium flex items-center justify-center"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Start Chat
                  </button>
                  <button 
                    onClick={() => setShowCaseModal(true)}
                    className="w-full px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 text-sm font-medium flex items-center justify-center"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Case
                  </button>
                  <button 
                    onClick={() => setShowMeetingModal(true)}
                    className="w-full px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 text-sm font-medium flex items-center justify-center"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Schedule Meeting
                  </button>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-xs text-gray-600 mb-2">Tags</div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">VIP</span>
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Tech Savvy</span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">Decision Maker</span>
                    <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">Renewal Soon</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Products & Subscriptions */}
            <div className="bg-white rounded-lg shadow p-5 border border-gray-200 mb-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <Star className="w-5 h-5 mr-2 text-yellow-600" />
                Active Products & Subscriptions
              </h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">Enterprise Suite v12.4</div>
                    <div className="text-sm text-gray-600 mt-1">100 licenses • Includes all modules</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-900">$8,500/month</div>
                    <div className="text-xs text-gray-600">Renews: Dec 31, 2025</div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">Premium Support Package</div>
                    <div className="text-sm text-gray-600 mt-1">24/7 support • Dedicated account manager</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-900">$2,000/month</div>
                    <div className="text-xs text-gray-600">Renews: Dec 31, 2025</div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-800">Advanced Analytics Module</div>
                    <div className="text-sm text-gray-600 mt-1">Real-time reporting • Custom dashboards</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-900">$1,500/month</div>
                    <div className="text-xs text-gray-600">Renews: Dec 31, 2025</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 mb-6">
              {/* Recent Interactions */}
              <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
                <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <Activity className="w-5 h-5 mr-2 text-teal-600" />
                  Recent Interactions
                </h2>
                <div className="space-y-3">
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200 border-l-4">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center">
                        <Phone className="w-4 h-4 text-red-600 mr-2" />
                        <span className="font-semibold text-gray-800 text-sm">Phone Call - Current</span>
                      </div>
                      <span className="text-xs text-gray-600">Now</span>
                    </div>
                    <div className="text-xs text-gray-700 ml-6">Issue: Slow performance in reporting module</div>
                    <div className="text-xs text-gray-600 ml-6 mt-1">Agent: John Smith (You)</div>
                  </div>
                  
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center">
                        <MessageSquare className="w-4 h-4 text-blue-600 mr-2" />
                        <span className="font-semibold text-gray-800 text-sm">Chat</span>
                      </div>
                      <span className="text-xs text-gray-600">Oct 22, 2025</span>
                    </div>
                    <div className="text-xs text-gray-700 ml-6">Issue: Password reset request</div>
                    <div className="text-xs text-gray-600 ml-6 mt-1">Agent: Emma Wilson • Duration: 8m</div>
                  </div>
                  
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center">
                        <Mail className="w-4 h-4 text-purple-600 mr-2" />
                        <span className="font-semibold text-gray-800 text-sm">Email</span>
                      </div>
                      <span className="text-xs text-gray-600">Oct 18, 2025</span>
                    </div>
                    <div className="text-xs text-gray-700 ml-6">Issue: Feature request - custom reports</div>
                    <div className="text-xs text-gray-600 ml-6 mt-1">Agent: Michael Chen • Resolved</div>
                  </div>
                  
                  <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center">
                        <Phone className="w-4 h-4 text-green-600 mr-2" />
                        <span className="font-semibold text-gray-800 text-sm">Phone Call</span>
                      </div>
                      <span className="text-xs text-gray-600">Sep 12, 2025</span>
                    </div>
                    <div className="text-xs text-gray-700 ml-6">Issue: Performance issues - Resolved</div>
                    <div className="text-xs text-gray-600 ml-6 mt-1">Agent: Sarah Martinez • Duration: 15m</div>
                  </div>
                </div>
              </div>

              {/* Open Cases */}
              <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
                <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <AlertTriangle className="w-5 h-5 mr-2 text-orange-600" />
                  Open Cases
                </h2>
                <div className="space-y-3">
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200 border-l-4">
                    <div className="flex items-start justify-between mb-1">
                      <div className="font-semibold text-gray-800 text-sm">CASE-3891</div>
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded font-medium">High</span>
                    </div>
                    <div className="text-sm text-gray-700 mb-1">Reporting module performance degradation</div>
                    <div className="text-xs text-gray-600">Opened: Oct 27, 2025 • Age: 4 hours</div>
                    <div className="text-xs text-gray-600 mt-1">Assigned to: John Smith (You)</div>
                  </div>
                  
                  <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200 border-l-4">
                    <div className="flex items-start justify-between mb-1">
                      <div className="font-semibold text-gray-800 text-sm">CASE-3845</div>
                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded font-medium">Medium</span>
                    </div>
                    <div className="text-sm text-gray-700 mb-1">Custom dashboard configuration help</div>
                    <div className="text-xs text-gray-600">Opened: Oct 25, 2025 • Age: 2 days</div>
                    <div className="text-xs text-gray-600 mt-1">Assigned to: Emma Wilson</div>
                  </div>
                  
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 border-l-4">
                    <div className="flex items-start justify-between mb-1">
                      <div className="font-semibold text-gray-800 text-sm">CASE-3802</div>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-medium">Low</span>
                    </div>
                    <div className="text-sm text-gray-700 mb-1">Documentation request for API integration</div>
                    <div className="text-xs text-gray-600">Opened: Oct 20, 2025 • Age: 7 days</div>
                    <div className="text-xs text-gray-600 mt-1">Assigned to: Michael Chen</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Activity Timeline */}
            <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
              <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-blue-600" />
                Activity Timeline & Notes
              </h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-800 text-sm">Currently on call</span>
                      <span className="text-xs text-gray-600">Oct 27, 2025 - 2:15 PM</span>
                    </div>
                    <div className="text-sm text-gray-700">Customer reporting slow performance in reporting module. Investigating database query optimization.</div>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-800 text-sm">Note added by Emma Wilson</span>
                      <span className="text-xs text-gray-600">Oct 22, 2025 - 10:30 AM</span>
                    </div>
                    <div className="text-sm text-gray-700">Customer requested password reset. Completed successfully. Reminded customer about MFA setup for enhanced security.</div>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-800 text-sm">Case resolved by Sarah Martinez</span>
                      <span className="text-xs text-gray-600">Sep 12, 2025 - 3:45 PM</span>
                    </div>
                    <div className="text-sm text-gray-700">Performance issue resolved by updating cache configuration. Customer confirmed improvement. Recommended quarterly system health checks.</div>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-800 text-sm">Account upgraded to Enterprise Premium</span>
                      <span className="text-xs text-gray-600">Jan 15, 2025</span>
                    </div>
                    <div className="text-sm text-gray-700">Customer upgraded from Enterprise Standard to Premium tier. Added 50 additional licenses and premium support package.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (tab.type === 'kb') {
      return (
        <div className="flex-1 bg-white p-6 overflow-auto">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center bg-gray-100 rounded-lg border border-gray-300 p-3 mb-6">
              <Search className="w-5 h-5 text-gray-400 mr-3" />
              <input type="text" placeholder="Search knowledge base..." className="flex-1 bg-transparent outline-none text-sm" defaultValue="enterprise performance slow reporting" />
            </div>
            
            <div className="space-y-3">
              <Tooltip content={
                <div>
                  <div className="font-bold mb-2">🤖 ML Model:</div>
                  <div className="mb-1"><strong>Knowledge Gap Detection</strong> (88% accuracy)</div>
                  <div className="text-xs mb-2">Analyzes customer query intent, interaction history, agent search patterns, and article effectiveness to recommend the most relevant KB content</div>
                  <div className="mt-2 pt-2 border-t border-gray-700">
                    <div className="font-bold mb-1">📊 Data Sources:</div>
                    <div className="text-xs">• <strong>Speech/Text Analytics API</strong> - Customer query intent extraction</div>
                    <div className="text-xs">• <strong>Interaction Context</strong> - Customer history and issue complexity</div>
                    <div className="text-xs">• <strong>KB Analytics</strong> - Article effectiveness metrics (views, helpful ratings)</div>
                    <div className="text-xs">• <strong>Agent Behavior</strong> - Historical search patterns and article usage</div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-700">
                    <div className="font-bold mb-1">💡 Use Case:</div>
                    <div className="text-xs"><strong>Dynamic Knowledge Recommendations</strong> - AI-powered article matching that learns from successful resolutions</div>
                  </div>
                </div>
              }>
                <div 
                  className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                    highlightKBArticle 
                      ? 'border-blue-400 bg-blue-50 shadow-lg animate-pulse' 
                      : 'border-blue-200 bg-blue-50 hover:shadow-md'
                  }`}
                  onClick={openKBArticleModal}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <Star className="w-4 h-4 text-yellow-500 mr-2 fill-current" />
                        <h3 className="font-semibold text-gray-800">{kbArticle.id}: {kbArticle.title}</h3>
                        <Info className="w-4 h-4 ml-2 text-blue-600" />
                        {highlightKBArticle && (
                          <div className="ml-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full animate-bounce">
                            Suggested by AI
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">Step-by-step guide for optimizing performance in enterprise reporting modules...</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded font-medium">Relevance: {kbArticle.relevance}%</span>
                        <span>Updated {kbArticle.lastUpdated}</span>
                        <span>👁️ {kbArticle.views} views</span>
                        <span>👍 {kbArticle.helpful} helpful</span>
                      </div>
                      <div className="flex gap-1">
                        <MLBadge model="KB Match" accuracy={88} />
                      </div>
                    </div>
                    <div className="ml-3 flex flex-col gap-2">
                      <button 
                        onClick={(e) => { e.stopPropagation(); openKBArticleModal(); }}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium flex items-center gap-1"
                      >
                        <BookOpen className="w-3 h-3" />
                        View Full Article
                      </button>
                    </div>
                  </div>
                </div>
              </Tooltip>

              <div className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow">
                <h3 className="font-semibold text-gray-800 mb-2">KB-2104: Database Query Optimization</h3>
                <p className="text-sm text-gray-600 mb-2">Learn how to optimize database queries for faster report generation...</p>
                <div className="flex items-center text-xs text-gray-500">
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded mr-2">Relevance: 85%</span>
                  <span>Updated 1 week ago</span>
                </div>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow">
                <h3 className="font-semibold text-gray-800 mb-2">KB-1923: Scaling Enterprise Reporting</h3>
                <p className="text-sm text-gray-600 mb-2">Best practices for scaling reporting infrastructure for growing user bases...</p>
                <div className="flex items-center text-xs text-gray-500">
                  <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded mr-2">Relevance: 78%</span>
                  <span>Updated 2 weeks ago</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (tab.type === 'google') {
      return (
        <div className="flex-1 bg-white overflow-auto">
          <div className="p-6">
            <div className="max-w-3xl mx-auto mt-8">
              <div className="mb-8 text-center">
                <span className="text-6xl font-bold">
                  <span className="text-blue-500">G</span>
                  <span className="text-red-500">o</span>
                  <span className="text-yellow-500">o</span>
                  <span className="text-blue-500">g</span>
                  <span className="text-green-500">l</span>
                  <span className="text-red-500">e</span>
                </span>
              </div>
              
              <div className="mb-6">
                <div className="flex items-center border border-gray-300 rounded-full px-4 py-3 shadow-md hover:shadow-lg transition-shadow">
                  <Search className="w-5 h-5 text-gray-400 mr-3" />
                  <input type="text" value={googleSearchQuery} onChange={(e) => setGoogleSearchQuery(e.target.value)} className="flex-1 outline-none text-sm" placeholder="Search Google" />
                </div>
                <div className="flex justify-center mt-6 space-x-3">
                  <button className="px-4 py-2 bg-gray-100 rounded text-sm text-gray-700 hover:bg-gray-200 border border-gray-300">Google Search</button>
                  <button className="px-4 py-2 bg-gray-100 rounded text-sm text-gray-700 hover:bg-gray-200 border border-gray-300">I'm Feeling Lucky</button>
                </div>
              </div>
              
              {googleSearchQuery && (
                <div className="mt-8">
                  <div className="text-sm text-gray-600 mb-4">About 2,450,000 results (0.48 seconds)</div>
                  <div className="space-y-6">
                    <div className="border-b border-gray-200 pb-4">
                      <div className="text-xs text-gray-600 mb-1">https://docs.enterprise-software.com</div>
                      <a href="#" className="text-xl text-blue-800 hover:underline">Enterprise Software Performance Optimization Guide</a>
                      <p className="text-sm text-gray-700 mt-1">Complete guide to optimizing enterprise software performance. Learn about database tuning, caching strategies...</p>
                    </div>
                    <div className="border-b border-gray-200 pb-4">
                      <div className="text-xs text-gray-600 mb-1">https://stackoverflow.com</div>
                      <a href="#" className="text-xl text-blue-800 hover:underline">How to improve reporting module performance</a>
                      <p className="text-sm text-gray-700 mt-1">I'm experiencing slow performance in our reporting module. Here are some solutions that worked...</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="flex-1 bg-white p-6 overflow-auto">
        <div className="text-center text-gray-500 mt-20">
          <ExternalLink className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className="text-sm">Web content would display here</p>
          <p className="text-xs mt-2 text-gray-400">{tab.url}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-white font-sans">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 text-white shadow-2xl" style={{paddingTop: '1rem', paddingBottom: '1rem'}}>
        <div className="px-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                  <Activity className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight">VERINT</h1>
                  <p className="text-xs text-blue-200 font-medium">Agent Workspace</p>
                </div>
              </div>
              <div className="h-10 w-px bg-blue-700"></div>
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${callDuration ? 'bg-red-400 animate-pulse' : 'bg-green-400'}`}></div>
                  <div>
                    <div className="text-xs text-blue-200">Status</div>
                    <div className="text-sm font-semibold">{callDuration ? 'On Call' : 'Available'}</div>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-blue-200">Agent</div>
                  <div className="text-sm font-semibold">John Smith</div>
                </div>
                <div>
                  <div className="text-xs text-blue-200">Team</div>
                  <div className="text-sm font-semibold">Alpha - Technical</div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-6">
              {callDuration > 0 && (
                <Tooltip content={
                  <div>
                    <div className="font-bold mb-2">🤖 ML Models Active on This Call:</div>
                    <div className="space-y-1 text-xs">
                      <div>• <strong>Customer-Agent Compatibility</strong> (94%) - Why you received this call</div>
                      <div>• <strong>Outcome Prediction</strong> - Predicting 87% FCR, 4.5 CSAT</div>
                      <div>• <strong>Sentiment Trajectory</strong> (86%) - Real-time customer emotion tracking</div>
                      <div>• <strong>Issue Prevention Detection</strong> (91%) - Proactive problem identification</div>
                      <div>• <strong>Churn Risk Scoring</strong> (89%) - Customer retention monitoring</div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-700 text-xs">
                      Click "View Details" in right panel for complete ML architecture breakdown
                    </div>
                  </div>
                }>
                  <div className="bg-red-500 bg-opacity-20 backdrop-blur-sm px-4 py-3 rounded-lg border border-red-400 cursor-help hover:bg-red-500 hover:bg-opacity-30 transition-colors">
                    <div className="flex items-center space-x-3 mb-2">
                      <Phone className="w-5 h-5 text-red-300" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-red-200 flex items-center gap-1">
                            Active Call
                            <Info className="w-3 h-3" />
                          </div>
                          <div className="flex items-center gap-1 text-xs text-purple-200 bg-purple-600 bg-opacity-40 px-2 py-0.5 rounded">
                            <Brain className="w-3 h-3" />
                            <span>AI Routed: {routingAnalysis.compatibilityScore}%</span>
                          </div>
                        </div>
                        <div className="text-lg font-bold font-mono">{formatTime(callDuration)}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 mt-2">
                      <button className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 rounded text-xs flex items-center border border-white border-opacity-30">
                        Mute
                      </button>
                      <button className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 rounded text-xs flex items-center border border-white border-opacity-30">
                        Hold
                      </button>
                      <button className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 rounded text-xs flex items-center border border-white border-opacity-30">
                        Transfer
                      </button>
                      <button className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 rounded text-xs flex items-center border border-white border-opacity-30">
                        Keypad
                      </button>
                      <button className="px-2 py-1 bg-red-600 hover:bg-red-700 rounded text-xs font-semibold flex items-center border border-red-500">
                        <X className="w-3 h-3 mr-1" />
                        End
                      </button>
                    </div>
                  </div>
                </Tooltip>
              )}
              
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <div className="text-xs text-blue-200">Quality Score</div>
                  <div className="text-xl font-bold">94%</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-blue-200">Today's Calls</div>
                  <div className="text-xl font-bold">23</div>
                </div>
              </div>
              
              <div className={`flex items-center px-3 py-2 rounded-lg ${callDuration ? 'bg-red-500 bg-opacity-20' : 'bg-green-500 bg-opacity-20'} backdrop-blur-sm`}>
                <div className={`w-2 h-2 rounded-full mr-2 ${callDuration ? 'bg-red-400 animate-pulse' : 'bg-green-400'}`}></div>
                <span className="text-xs font-semibold">QM Recording</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center space-x-4 text-blue-200">
              <div className="flex items-center space-x-1">
                <Clock className="w-3 h-3" />
                <span>Shift: 8:00 AM - 5:00 PM</span>
              </div>
              <div className="flex items-center space-x-1">
                <Target className="w-3 h-3" />
                <span>Next Break: 2:30 PM</span>
              </div>
              <div className="flex items-center space-x-1">
                <Activity className="w-3 h-3" />
                <span>Queue: 12 waiting</span>
              </div>
            </div>
            <div className="text-blue-300">
              Verint Workforce Optimization Platform v24.3 • Connected
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-gray-100 border-b border-gray-300">
        <div className="flex items-end px-2 pt-1">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              draggable
              onDragStart={(e) => handleDragStart(e, tab.id)}
              onDragOver={(e) => handleDragOver(e, tab.id)}
              onDragEnd={handleDragEnd}
              onClick={() => { setActiveTab(tab.id); setAddressBarUrl(tab.url); }}
              className={`flex items-center px-3 py-2 rounded-t cursor-move transition-all min-w-[140px] max-w-[240px] group ${
                activeTab === tab.id 
                  ? 'bg-white text-gray-800 border-t border-l border-r border-gray-300' 
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              } ${dragOverTab === tab.id && draggedTab !== tab.id ? 'border-l-4 border-l-blue-500' : ''}`}
            >
              <GripVertical className="w-3 h-3 text-gray-400 mr-1 flex-shrink-0" />
              <span className="text-sm flex-1 truncate mr-2">{tab.title}</span>
              {tab.canClose && (
                <button
                  onClick={(e) => { e.stopPropagation(); closeTab(tab.id); }}
                  className="opacity-0 group-hover:opacity-100 hover:bg-gray-300 rounded p-0.5 flex-shrink-0"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
          <button onClick={() => addTab('New Tab', 'about:blank', 'web')} className="ml-1 p-2 hover:bg-gray-200 rounded-t">
            <Plus className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Address Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2">
        <div className="flex items-center space-x-2">
          <button className="p-1.5 hover:bg-gray-100 rounded"><ArrowLeft className="w-4 h-4 text-gray-600" /></button>
          <button className="p-1.5 hover:bg-gray-100 rounded"><ArrowRight className="w-4 h-4 text-gray-400" /></button>
          <button className="p-1.5 hover:bg-gray-100 rounded"><RefreshCw className="w-4 h-4 text-gray-600" /></button>
          <button className="p-1.5 hover:bg-gray-100 rounded"><Home className="w-4 h-4 text-gray-600" /></button>
          
          <div className="flex-1 flex items-center bg-gray-100 rounded-full px-4 py-2 hover:bg-gray-200 border border-gray-300">
            <Lock className="w-4 h-4 text-gray-500 mr-2" />
            <input type="text" value={addressBarUrl} onChange={(e) => setAddressBarUrl(e.target.value)} className="flex-1 bg-transparent outline-none text-sm text-gray-700" />
          </div>
          
          <button onClick={() => setShowLeftPanel(!showLeftPanel)} className="p-1.5 hover:bg-gray-100 rounded">
            <Menu className="w-4 h-4 text-gray-600" />
          </button>
          
          <button onClick={() => setShowRightPanel(!showRightPanel)} className="p-1.5 hover:bg-gray-100 rounded">
            {showRightPanel ? <PanelRightClose className="w-4 h-4 text-gray-600" /> : <PanelRightOpen className="w-4 h-4 text-gray-600" />}
          </button>
          
          <button className="p-1.5 hover:bg-gray-100 rounded"><MoreVertical className="w-4 h-4 text-gray-600" /></button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel */}
        {showLeftPanel && (
          <div className="w-80 bg-gray-50 border-r border-gray-200 flex flex-col overflow-hidden">
            <div className="bg-white border-b border-gray-200 flex">
              <button
                onClick={() => setLeftPanelTab('transcript')}
                className={`flex-1 px-4 py-3 text-sm font-semibold flex items-center justify-center border-b-2 transition-colors ${
                  leftPanelTab === 'transcript'
                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Live Transcript
              </button>
              <button
                onClick={() => setLeftPanelTab('chat')}
                className={`flex-1 px-4 py-3 text-sm font-semibold flex items-center justify-center border-b-2 transition-colors ${
                  leftPanelTab === 'chat'
                    ? 'border-purple-600 text-purple-600 bg-purple-50'
                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Live Chat
                {chatMessages.length > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 bg-purple-600 text-white text-xs rounded-full">
                    {chatMessages.length}
                  </span>
                )}
              </button>
            </div>

            {leftPanelTab === 'transcript' && (
              <>
                <div className="border-b border-gray-200 p-4 bg-white">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <BookOpen className="w-4 h-4 mr-2 text-blue-600" />
                    Active Playbook
                  </h3>
                  <div className="text-xs text-blue-600 font-medium mb-3">{playbook.name}</div>
                  <div className="space-y-2">
                    {playbook.steps.map((step) => (
                      <div key={step.id} className={`flex items-start p-2 rounded ${step.status === 'completed' ? 'bg-green-50 border border-green-200' : step.status === 'active' ? 'bg-blue-50 border border-blue-200' : 'bg-white border border-gray-200'}`}>
                        <div className="mr-2 mt-0.5 flex-shrink-0">
                          {step.status === 'completed' ? <CheckCircle className="w-4 h-4 text-green-600" /> : step.status === 'active' ? <Clock className="w-4 h-4 text-blue-600" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300"></div>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800">{step.title}</div>
                          {step.duration && <div className="text-xs text-gray-500 mt-0.5">{step.duration}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex-1 overflow-auto p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                    <MessageSquare className="w-4 h-4 mr-2 text-blue-600" />
                    Call Transcript
                  </h3>
                  <div className="space-y-3">
                    {transcript.map((item, idx) => (
                      <div key={idx}>
                        <div className="flex items-center mb-1">
                          <span className={`text-xs font-semibold ${item.speaker === 'Customer' ? 'text-purple-600' : 'text-blue-600'}`}>{item.speaker}</span>
                          <span className="text-xs text-gray-400 ml-2">{item.time}</span>
                        </div>
                        <div className="text-sm text-gray-700 bg-white p-2 rounded border border-gray-200">{item.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {leftPanelTab === 'chat' && (
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="p-3 bg-gradient-to-r from-purple-100 to-purple-50 border-b border-purple-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-gray-800 text-sm">Sarah Johnson</div>
                      <div className="text-xs text-gray-600">TechCorp Solutions</div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-xs text-gray-600">Online</span>
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-auto p-3 bg-gray-50">
                  {chatMessages.length === 0 ? (
                    <div className="text-center text-gray-500 py-10">
                      <MessageSquare className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                      <p className="text-sm">No active chat</p>
                      <p className="text-xs mt-1">Click "Start Chat" in CRM to begin</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {chatMessages.map((msg, idx) => (
                        <div key={idx} className={`flex ${msg.role === 'agent' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] p-2 rounded-lg text-sm ${
                            msg.role === 'agent' 
                              ? 'bg-purple-600 text-white' 
                              : 'bg-white border border-gray-200 text-gray-800'
                          }`}>
                            <div className="text-sm">{msg.text}</div>
                            <div className={`text-xs mt-1 ${msg.role === 'agent' ? 'text-purple-200' : 'text-gray-500'}`}>
                              {msg.time}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-200 p-3 bg-white">
                  <div className="flex space-x-2">
                    <input 
                      type="text"
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                      placeholder="Type your message..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-purple-500"
                    />
                    <button 
                      onClick={sendChatMessage}
                      className="px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {tabs.find(t => t.id === activeTab) && renderTabContent(tabs.find(t => t.id === activeTab))}
        </div>

        {/* Right Panel */}
        {showRightPanel && (
          <div className="w-96 bg-gray-50 border-l border-gray-200 flex flex-col overflow-hidden">
            {/* Routing Intelligence Panel */}
            {showRoutingIntelligence && (
              <Tooltip content={
                <div>
                  <div className="font-bold mb-2">🤖 Intelligent CX Routing System</div>
                  <div className="text-xs mb-2">10+ ML models processed 50+ data signals in {routingAnalysis.routingTime}ms to match you with this customer</div>
                  <div className="space-y-1 mb-2">
                    <div className="text-xs"><strong>Why you?</strong></div>
                    <div className="text-xs">• 95% technical proficiency match</div>
                    <div className="text-xs">• Top 5% empathy scores in team</div>
                    <div className="text-xs">• 87% historical FCR with frustrated customers</div>
                    <div className="text-xs">• Personality compatibility: 94%</div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-gray-700">
                    <div className="text-xs font-bold mb-1">💡 Use Case:</div>
                    <div className="text-xs"><strong>Intelligent CX Routing</strong> - AI-powered customer-agent matching for optimal outcomes</div>
                  </div>
                </div>
              }>
                <div className="border-b border-gray-200 bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-700 text-white cursor-help hover:from-purple-700 hover:to-indigo-800 transition-colors">
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xs font-semibold flex items-center">
                        <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                        Intelligent Routing Analysis
                        <Info className="w-3 h-3 ml-1.5" />
                      </h3>
                      <div className="flex items-center gap-1">
                        <div className="px-1.5 py-0.5 bg-white bg-opacity-20 text-white text-xs rounded font-medium">
                          {routingAnalysis.routingTime}ms
                        </div>
                        <button 
                          onClick={() => setShowRoutingIntelligence(false)}
                          className="hover:bg-white hover:bg-opacity-20 rounded p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-3 mb-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs text-purple-100">Customer-Agent Match</div>
                        <Award className="w-4 h-4 text-yellow-300" />
                      </div>
                      <div className="flex items-end gap-2">
                        <div className="text-3xl font-bold">{routingAnalysis.compatibilityScore}%</div>
                        <div className="text-xs text-purple-100 pb-1">Compatibility</div>
                      </div>
                      <div className="text-xs text-purple-200 mt-1">
                        🎯 Optimal match based on personality, expertise, and past outcomes
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded p-2 text-center">
                        <div className="text-lg font-bold">{routingAnalysis.predictedCSAT}</div>
                        <div className="text-xs text-purple-100">Pred. CSAT</div>
                      </div>
                      <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded p-2 text-center">
                        <div className="text-lg font-bold">{routingAnalysis.predictedFCR}%</div>
                        <div className="text-xs text-purple-100">Pred. FCR</div>
                      </div>
                      <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded p-2 text-center">
                        <div className="text-lg font-bold">{routingAnalysis.predictedAHT}m</div>
                        <div className="text-xs text-purple-100">Pred. AHT</div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-purple-100">
                      <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                        {routingAnalysis.modelsProcessed} ML Models
                      </div>
                      <div>{routingAnalysis.signalsAnalyzed} Signals Analyzed</div>
                    </div>
                    
                    <button
                      onClick={() => setShowRoutingDetails(true)}
                      className="w-full mt-2 px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 rounded text-xs font-medium flex items-center justify-center gap-1 transition-colors"
                    >
                      <Layers className="w-3 h-3" />
                      View Routing Intelligence Details
                    </button>
                  </div>
                  
                  <div className="bg-purple-800 bg-opacity-50 border-t border-purple-500 border-opacity-30 p-2">
                    <details className="group">
                      <summary className="cursor-pointer text-xs font-semibold text-purple-100 flex items-center justify-between">
                        <span className="flex items-center">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          Why You Received This Interaction
                        </span>
                        <ChevronRight className="w-3 h-3 group-open:rotate-90 transition-transform" />
                      </summary>
                      <div className="mt-2 text-xs text-purple-100 space-y-1 pl-4">
                        <div className="flex items-start gap-1">
                          <div className="w-1 h-1 bg-purple-300 rounded-full mt-1.5"></div>
                          <div>High empathy scores (Top 5% in team)</div>
                        </div>
                        <div className="flex items-start gap-1">
                          <div className="w-1 h-1 bg-purple-300 rounded-full mt-1.5"></div>
                          <div>Technical expertise in this product (95% FCR)</div>
                        </div>
                        <div className="flex items-start gap-1">
                          <div className="w-1 h-1 bg-purple-300 rounded-full mt-1.5"></div>
                          <div>Personality match: Customer prefers direct communication</div>
                        </div>
                        <div className="flex items-start gap-1">
                          <div className="w-1 h-1 bg-purple-300 rounded-full mt-1.5"></div>
                          <div>Historical success: 87% FCR with frustrated customers</div>
                        </div>
                      </div>
                    </details>
                  </div>
                </div>
              </Tooltip>
            )}
            
            {/* Market Intelligence Alert */}
            <Tooltip content={
              <div>
                <div className="font-bold mb-2">🤖 ML Model:</div>
                <div className="mb-1"><strong>Market Intelligence Integration</strong> (82% accuracy)</div>
                <div className="text-xs mb-2">Analyzes social media, news, and public data to identify events that may impact customer sentiment</div>
                <div className="mt-2 pt-2 border-t border-gray-700">
                  <div className="font-bold mb-1">📊 Data Sources:</div>
                  <div className="text-xs">• Social media monitoring APIs</div>
                  <div className="text-xs">• News aggregation feeds</div>
                  <div className="text-xs">• Customer sentiment pattern analysis</div>
                  <div className="text-xs">• Product/service event tracking</div>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-700">
                  <div className="font-bold mb-1">💡 Use Case:</div>
                  <div className="text-xs"><strong>Real-Time Market Intelligence</strong> - External event awareness for proactive customer service</div>
                </div>
              </div>
            }>
              <div className="border-b border-gray-200 p-3 bg-gradient-to-r from-orange-50 to-red-50 border-l-4 border-orange-500 cursor-help hover:from-orange-100 hover:to-red-100 transition-colors">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-orange-900 mb-1 flex items-center gap-1">
                      Market Event Alert
                      <Info className="w-3 h-3" />
                    </div>
                    <div className="text-xs text-orange-800">
                      Product recall announced 2hrs ago via social media. Customer likely aware. Empathy approach recommended.
                    </div>
                    <div className="text-xs text-orange-600 mt-1">
                      📊 Churn risk increased from 12% → 35%
                    </div>
                  </div>
                </div>
              </div>
            </Tooltip>
            
            <Tooltip content={
              <div>
                <div className="font-bold mb-2">🤖 ML Models Active:</div>
                <div className="space-y-1 text-xs mb-2">
                  <div>• <strong>Next Best Action Engine</strong> (85% accuracy)</div>
                  <div className="ml-4">Recommends optimal actions based on customer context, interaction history, and successful resolution patterns</div>
                  <div>• <strong>Knowledge Gap Detection</strong> (88% accuracy)</div>
                  <div className="ml-4">Identifies relevant KB articles matching issue complexity</div>
                  <div>• <strong>Issue Prevention Detection</strong> (91% accuracy)</div>
                  <div className="ml-4">Proactive problem identification before escalation</div>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-700">
                  <div className="font-bold mb-1">📊 Data Sources:</div>
                  <div className="text-xs">• Speech/Text Analytics API (intent detection)</div>
                  <div className="text-xs">• Interaction history (customer context)</div>
                  <div className="text-xs">• KB analytics (article effectiveness metrics)</div>
                  <div className="text-xs">• Quality Management (best practice patterns)</div>
                </div>
              </div>
            }>
              <div className="border-b border-gray-200 p-3 bg-gradient-to-br from-blue-50 to-indigo-50 cursor-help hover:from-blue-100 hover:to-indigo-100 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-gray-800 flex items-center">
                    <Zap className="w-3.5 h-3.5 mr-1.5 text-blue-600" />
                    Copilot Suggestions
                    <Info className="w-3 h-3 ml-1.5 text-blue-600" />
                  </h3>
                  <div className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded font-medium">Active</div>
                </div>
                <div className="space-y-1.5">
                  <div className="p-2 rounded text-xs bg-white border border-purple-300">
                    <div className="flex items-start justify-between mb-1">
                      <div className="font-medium text-xs text-gray-800 flex-1">Address Product Recall First</div>
                      <div className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium ml-2">98%</div>
                    </div>
                    <div className="text-xs text-gray-600">Acknowledge recall proactively. Customer hasn't mentioned yet but is likely aware.</div>
                  </div>
                  <div className="p-2 rounded text-xs bg-white border border-blue-300">
                    <div className="flex items-start justify-between mb-1">
                      <div className="font-medium text-xs text-gray-800 flex-1">Recommend Product Upgrade</div>
                      <div className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-medium ml-2">92%</div>
                    </div>
                    <div className="text-xs text-gray-600">Customer mentioned performance 3x</div>
                  </div>
                  <div className="p-2 rounded text-xs bg-white border border-green-300">
                    <div className="flex items-start justify-between mb-1">
                      <div className="font-medium text-xs text-gray-800 flex-1">KB-1847: Performance Guide</div>
                      <div className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-medium ml-2">88%</div>
                    </div>
                    <div className="flex gap-1">
                      <button 
                        onClick={openKBArticle}
                        className="flex-1 text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700 transition-colors"
                      >
                        Go to Article
                      </button>
                      <button 
                        onClick={openKBArticleModal}
                        className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 transition-colors"
                      >
                        Quick View
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Tooltip>

            <div className="flex-1 flex flex-col overflow-hidden border-b border-gray-200 bg-white">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3 py-2 flex items-center justify-between">
                <div className="flex items-center">
                  <Sparkles className="w-4 h-4 mr-2" />
                  <span className="font-semibold text-sm">AI Assistant</span>
                </div>
              </div>
              
              <div className="flex-1 overflow-auto p-3 bg-gray-50">
                <div className="space-y-2">
                  {llmMessages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[90%] p-2 rounded text-xs ${msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-800'}`}>
                        {msg.content}
                        {msg.content.includes('KB-1847') && msg.role === 'assistant' && (
                          <button
                            onClick={openKBArticleModal}
                            className="mt-2 w-full px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs flex items-center justify-center gap-1"
                          >
                            <BookOpen className="w-3 h-3" />
                            View KB-1847
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="border-t border-gray-200 p-2 bg-white">
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={llmInput}
                    onChange={(e) => setLlmInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendLLMMessage()}
                    placeholder="Ask AI assistant..."
                    className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-xs outline-none focus:border-blue-500"
                  />
                  <button onClick={sendLLMMessage} className="px-2 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs flex items-center flex-shrink-0">
                    <Send className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>

            <Tooltip content={
              <div>
                <div className="font-bold mb-2">💡 Use Case:</div>
                <div className="mb-1"><strong>Customer Journey Analytics with Workforce Context</strong></div>
                <div className="text-xs mb-2">Unified view combining customer profile data with real-time agent interaction context</div>
                <div className="mt-2 pt-2 border-t border-gray-700">
                  <div className="font-bold mb-1">🤖 ML Models:</div>
                  <div className="text-xs">• Churn Risk Scoring (89% accuracy) - 35% risk</div>
                  <div className="text-xs">• Lifetime Value Forecasting (92% accuracy) - $487K LTV</div>
                </div>
              </div>
            }>
              <div className="p-3 border-b border-gray-200 bg-white cursor-help hover:bg-gray-50 transition-colors">
                <h3 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                  <User className="w-3 h-3 mr-1.5 text-blue-600" />
                  Customer
                  <Info className="w-3 h-3 ml-2 text-blue-500" />
                </h3>
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-gray-600">Sarah Johnson</div>
                    <div className="text-xs text-gray-500">ACC-94821</div>
                  </div>
                  <div className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs font-medium inline-block">Enterprise Premium</div>
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">Churn Risk</span>
                      <span className="font-semibold text-orange-600">35%</span>
                    </div>
                    <div className="flex items-center justify-between text-xs mt-1">
                      <span className="text-gray-600">LTV</span>
                      <span className="font-semibold text-green-600">$487K</span>
                    </div>
                  </div>
                </div>
              </div>
            </Tooltip>

            <Tooltip content={
              <div>
                <div className="font-bold mb-2">📊 Real-Time Operational Analytics</div>
                <div className="text-xs mb-2">Live metrics showing interaction efficiency and effectiveness</div>
                <div className="mt-2 pt-2 border-t border-gray-700">
                  <div className="font-bold mb-1">💡 Use Case:</div>
                  <div className="text-xs"><strong>Real-Time Operational Alerts</strong></div>
                  <div className="text-xs">Proactive monitoring with anomaly detection and alerts via workflow integration</div>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-700">
                  <div className="font-bold mb-1">📊 Data Source:</div>
                  <div className="text-xs">Contact Center Statistics API - real-time metrics</div>
                </div>
              </div>
            }>
              <div className="p-3 bg-white cursor-help hover:bg-gray-50 transition-colors">
                <h3 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
                  <BarChart3 className="w-3 h-3 mr-1.5 text-blue-600" />
                  Interaction Metrics
                  <Info className="w-3 h-3 ml-2 text-blue-500" />
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-gray-50 p-1.5 rounded border border-gray-200">
                    <div className="text-xs text-gray-600">Tab Switches</div>
                    <div className="text-sm font-bold text-gray-800">5</div>
                  </div>
                  <div className="bg-gray-50 p-1.5 rounded border border-gray-200">
                    <div className="text-xs text-gray-600">Searches</div>
                    <div className="text-sm font-bold text-gray-800">3</div>
                  </div>
                </div>
                
                <div className="mt-2 p-2 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded text-xs">
                  <div className="flex items-center gap-1.5 text-purple-700">
                    <div className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-pulse"></div>
                    <span className="font-medium">Routing AI Learning</span>
                  </div>
                  <div className="text-purple-600 mt-0.5">System improving from this interaction</div>
                </div>
              </div>
            </Tooltip>
          </div>
        )}
      </div>
      
      {/* Modals */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-auto">
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <Mail className="w-6 h-6 mr-3" />
                <h2 className="text-xl font-bold">Compose Email</h2>
              </div>
              <button onClick={() => setShowEmailModal(false)} className="hover:bg-green-800 rounded p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">To:</label>
                <input 
                  type="email" 
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Subject:</label>
                <input 
                  type="text" 
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Enter email subject"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-green-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Message:</label>
                <textarea 
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  placeholder="Type your message here..."
                  rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-green-500 resize-none"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button 
                  onClick={() => setShowEmailModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-medium"
                >
                  Cancel
                </button>
                <button 
                  onClick={sendEmail}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium flex items-center"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showCaseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="bg-gradient-to-r from-orange-600 to-orange-700 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <Plus className="w-6 h-6 mr-3" />
                <h2 className="text-xl font-bold">Create New Case</h2>
              </div>
              <button onClick={() => setShowCaseModal(false)} className="hover:bg-orange-800 rounded p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Customer:</label>
                <input 
                  type="text" 
                  value="Sarah Johnson - TechCorp Solutions"
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Case Title:</label>
                <input 
                  type="text" 
                  value={caseTitle}
                  onChange={(e) => setCaseTitle(e.target.value)}
                  placeholder="Brief description of the issue"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-orange-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Priority:</label>
                  <select 
                    value={casePriority}
                    onChange={(e) => setCasePriority(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-orange-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Category:</label>
                  <select 
                    value={caseCategory}
                    onChange={(e) => setCaseCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-orange-500"
                  >
                    <option value="technical">Technical Support</option>
                    <option value="billing">Billing</option>
                    <option value="general">General Inquiry</option>
                    <option value="feature">Feature Request</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description:</label>
                <textarea 
                  value={caseDescription}
                  onChange={(e) => setCaseDescription(e.target.value)}
                  placeholder="Detailed description of the issue..."
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-orange-500 resize-none"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button 
                  onClick={() => setShowCaseModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-medium"
                >
                  Cancel
                </button>
                <button 
                  onClick={createCase}
                  className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 font-medium flex items-center"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Case
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showMeetingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <Clock className="w-6 h-6 mr-3" />
                <h2 className="text-xl font-bold">Schedule Meeting</h2>
              </div>
              <button onClick={() => setShowMeetingModal(false)} className="hover:bg-teal-800 rounded p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Attendee:</label>
                <input 
                  type="text" 
                  value="Sarah Johnson (sarah.johnson@techcorp.com)"
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded bg-gray-100 text-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Subject:</label>
                <input 
                  type="text" 
                  value={meetingSubject}
                  onChange={(e) => setMeetingSubject(e.target.value)}
                  placeholder="Meeting subject"
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date:</label>
                  <input 
                    type="date" 
                    value={meetingDate}
                    onChange={(e) => setMeetingDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Time:</label>
                  <input 
                    type="time" 
                    value={meetingTime}
                    onChange={(e) => setMeetingTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Duration:</label>
                <select 
                  value={meetingDuration}
                  onChange={(e) => setMeetingDuration(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-teal-500"
                >
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">1 hour</option>
                  <option value="90">1.5 hours</option>
                  <option value="120">2 hours</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button 
                  onClick={() => setShowMeetingModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 font-medium"
                >
                  Cancel
                </button>
                <button 
                  onClick={scheduleMeeting}
                  className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 font-medium flex items-center"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Schedule Meeting
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showCallModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center">
                <Phone className="w-6 h-6 mr-3" />
                <h2 className="text-xl font-bold">Call Customer</h2>
              </div>
              <button onClick={() => setShowCallModal(false)} className="hover:bg-blue-800 rounded p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center text-3xl font-bold text-blue-600 mx-auto mb-4">
                  SJ
                </div>
                <h3 className="text-xl font-bold text-gray-800">Sarah Johnson</h3>
                <p className="text-gray-600 text-sm">TechCorp Solutions</p>
                <p className="text-gray-600 text-lg font-semibold mt-2">+1 (555) 123-4567</p>
              </div>
              
              <div className="space-y-3">
                <button 
                  onClick={() => {
                    alert('Initiating call to +1 (555) 123-4567...');
                    setShowCallModal(false);
                  }}
                  className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </button>
                <button 
                  onClick={() => setShowCallModal(false)}
                  className="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Routing Intelligence Details Modal */}
      {showRoutingDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-auto">
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 text-white px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center">
                <Brain className="w-6 h-6 mr-3" />
                <div>
                  <h2 className="text-xl font-bold">Intelligent CX Routing Analysis</h2>
                  <p className="text-sm text-purple-200">Complete ML Models & Data Insights Architecture</p>
                </div>
              </div>
              <button onClick={() => setShowRoutingDetails(false)} className="hover:bg-purple-800 rounded p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="font-bold text-blue-900 mb-2">📊 Complete ML & Data Insights Architecture Demonstrated</div>
                <div className="text-sm text-blue-800 mb-3">This interaction showcases the full power of Verint's headless analytics and ML model strategy across 10+ models and 50+ data signals:</div>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <div className="font-semibold text-blue-900 mb-2">🤖 10 ML Models Active:</div>
                    <div className="space-y-1 text-blue-700">
                      <div>1. Customer-Agent Compatibility (94%)</div>
                      <div>2. Interaction Outcome Prediction (87%)</div>
                      <div>3. Sentiment Trajectory Forecasting (86%)</div>
                      <div>4. Churn Risk Scoring (89%)</div>
                      <div>5. Lifetime Value Forecasting (92%)</div>
                      <div>6. Next Best Action Engine (85%)</div>
                      <div>7. Knowledge Gap Detection (88%)</div>
                      <div>8. Market Intelligence Integration (82%)</div>
                      <div>9. Demand Forecasting (91%)</div>
                      <div>10. Agent Performance Prediction (88%)</div>
                    </div>
                  </div>
                  <div>
                    <div className="font-semibold text-blue-900 mb-2">📊 Verint APIs Used:</div>
                    <div className="space-y-1 text-blue-700">
                      <div>• Workforce Analytics API</div>
                      <div>• Contact Center Statistics API</div>
                      <div>• Interaction Analytics API</div>
                      <div>• Quality Management API</div>
                      <div>• Speech/Text Analytics API</div>
                      <div>• Digital Experience API</div>
                      <div>• Knowledge Base API</div>
                      <div>• Customer Profile/CDP Integration</div>
                      <div>• External Data (weather, social, market signals)</div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-blue-300">
                  <div className="font-semibold text-blue-900 mb-2">💡 Use Cases Demonstrated:</div>
                  <div className="grid grid-cols-3 gap-2 text-xs text-blue-700">
                    <div>✓ Embedded Agent Performance Dashboard</div>
                    <div>✓ Intelligent CX Routing</div>
                    <div>✓ Predictive Staffing Intelligence</div>
                    <div>✓ Customer Journey Analytics</div>
                    <div>✓ Real-Time Operational Alerts</div>
                    <div>✓ Knowledge Gap Detection</div>
                    <div>✓ Market Intelligence Integration</div>
                    <div>✓ White-Label Analytics Potential</div>
                    <div>✓ Multi-Tenant Analytics Ready</div>
                  </div>
                </div>
              </div>

              <div className="text-center py-8">
                <Brain className="w-16 h-16 mx-auto mb-4 text-purple-600" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Complete ML Model Architecture</h3>
                <p className="text-gray-600 mb-4">10 parallel models analyzing 52+ data signals from multiple Verint APIs in real-time</p>
                <div className="flex justify-center gap-4 text-sm flex-wrap">
                  <div className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg font-medium">
                    {routingAnalysis.routingTime}ms Decision Time
                  </div>
                  <div className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-medium">
                    {routingAnalysis.compatibilityScore}% Match Score
                  </div>
                  <div className="px-4 py-2 bg-green-100 text-green-700 rounded-lg font-medium">
                    Predicted: {routingAnalysis.predictedFCR}% FCR
                  </div>
                  <div className="px-4 py-2 bg-teal-100 text-teal-700 rounded-lg font-medium">
                    Predicted: {routingAnalysis.predictedCSAT} CSAT
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setShowRoutingDetails(false)}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KB Article Modal */}
      {showKBArticle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-auto">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center">
                <BookOpen className="w-6 h-6 mr-3" />
                <div>
                  <h2 className="text-xl font-bold">{kbArticle.id}: {kbArticle.title}</h2>
                  <p className="text-sm text-blue-200">Suggested by Knowledge Gap Detection ML Model (88% accuracy)</p>
                </div>
              </div>
              <button onClick={() => setShowKBArticle(false)} className="hover:bg-blue-800 rounded p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Brain className="w-5 h-5 text-green-600" />
                  <div className="font-bold text-green-900">AI-Powered Article Recommendation</div>
                </div>
                <div className="text-sm text-green-800">
                  This article was selected with 97% relevance based on analyzing the customer's query intent, 
                  interaction history, and similar successful resolutions. The Knowledge Gap Detection model 
                  processed speech analytics, customer context, and KB effectiveness metrics to find the optimal match.
                </div>
              </div>

              <div className="prose max-w-none">
                <div className="whitespace-pre-wrap text-sm text-gray-700 font-mono bg-gray-50 p-4 rounded border border-gray-200">{kbArticle.content}</div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowKBArticle(false)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerintBrowser;

// Re-export as App for compatibility
export { VerintBrowser as App };